import { useCallback } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';

import { useAutoCheckOffTask } from 'src/app/pages/starterDashboardV2/shared/hooks';
import { getFile } from 'src/app/shared/hooks/file';
import { shareNative } from 'src/app/shared/share/mobileNativeShare';
import { copyToClipboard } from 'src/app/shared/utils/clipboard';

import { useShareUrlWithUtmParams } from '../../utils/getShareUrlWithUtmParams';
import { stripQuery } from '../../utils/stripQuery';

import type { ActionsReturnType, StorySharingHookProps } from './types';

const TRACKING_EVENT_NAME = 'share_petition';
const SHARING_EVENT_PREFIX = 'native_share_ready_to_share_facebook_story';

export function useFacebookStorySharing(props: StorySharingHookProps): ActionsReturnType {
	const track = useTracking();
	const i18n = useI18n();
	const autoCheckOffTask = useAutoCheckOffTask();

	const {
		previewImageUrl,
		shareUrl,
		trackingProps: { context, viewContext },
	} = props;

	const shareUrlWithUtmParams = useShareUrlWithUtmParams(shareUrl, 'facebook_story');

	const primaryAction = useCallback(async () => {
		void copyToClipboard(shareUrlWithUtmParams || shareUrl, i18n);
		const image = await getFile(stripQuery(previewImageUrl));
		const trackingData = {
			context,
			location: 'facebook_story',
			tab_at_share: viewContext,
			content: 'post',
			app_available: false,
		};

		if (!image) {
			return;
		}

		void track(TRACKING_EVENT_NAME, trackingData);
		await shareNative(
			{ files: [image] },
			{
				trackingEvents: { success: `${SHARING_EVENT_PREFIX}_success`, failure: `${SHARING_EVENT_PREFIX}_failure` },
				trackingData,
			},
			track,
		);
		await autoCheckOffTask('share_facebook');
	}, [context, i18n, previewImageUrl, shareUrl, shareUrlWithUtmParams, track, viewContext, autoCheckOffTask]);

	return {
		primaryAction,
		secondaryAction: undefined,
	};
}
